import { useState, useEffect } from 'react';
import AppService from '../Configurations/Appservice';

/**
 * Custom hook for fetching transaction audit trail
 * Phase 8.2 Implementation - UPDATED VERSION
 *
 * @param {string} transactionId - The transaction ID to fetch audit for
 * @param {boolean} enabled - Whether to fetch data (default: true)
 * @returns {object} { auditTrail, loading, error, refetch }
 */
export const useTransactionAudit = (transactionId, enabled = true) => {
  console.log('[useTransactionAudit] Hook initialized with transactionId:', transactionId);
  const [auditTrail, setAuditTrail] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAuditTrail = async () => {
    if (!transactionId || !enabled) return;

    try {
      setLoading(true);
      setError(null);

      console.log('[AuditTrail] Fetching audit for transaction:', transactionId);

      const response = await AppService.get(`/v1/transactions/${transactionId}/audit`);

      console.log('[AuditTrail] Response received:', response);
      console.log('[AuditTrail] Response type:', typeof response);
      console.log('[AuditTrail] Response.success:', response?.success);
      console.log('[AuditTrail] Response.data:', response?.data);
      console.log('[AuditTrail] Response.data type:', typeof response?.data);
      console.log('[AuditTrail] Response.data isArray:', Array.isArray(response?.data));

      // AppService interceptor unwraps response.data, so response is already the data object
      if (response && response.success && response.data) {
        console.log('[AuditTrail] Setting audit trail data with length:', response.data.length);
        console.log('[AuditTrail] First audit record:', response.data[0]);
        setAuditTrail(response.data);
      } else {
        console.log('[AuditTrail] Response not successful or no data', response);
        setAuditTrail([]);
      }
    } catch (err) {
      console.error('[AuditTrail] Failed to fetch audit trail:', err);
      console.error('[AuditTrail] Error response:', err.response);
      setError(err.response?.data?.message || 'Failed to fetch audit trail');
      setAuditTrail([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditTrail();
  }, [transactionId, enabled]);

  return {
    auditTrail,
    loading,
    error,
    refetch: fetchAuditTrail,
  };
};

/**
 * Hook for fetching audit records by field
 */
export const useTransactionAuditByField = (transactionId, fieldName, enabled = true) => {
  const [auditRecords, setAuditRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFieldAudit = async () => {
    if (!transactionId || !fieldName || !enabled) return;

    try {
      setLoading(true);
      setError(null);

      const response = await AppService.get(`/v1/transactions/${transactionId}/audit/field/${fieldName}`);

      // AppService interceptor unwraps response.data, so response is already the data object
      if (response && response.success) {
        setAuditRecords(response.data || []);
      } else {
        setAuditRecords([]);
      }
    } catch (err) {
      console.error('Failed to fetch field audit:', err);
      setError(err.response?.data?.message || 'Failed to fetch field audit');
      setAuditRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFieldAudit();
  }, [transactionId, fieldName, enabled]);

  // eslint-disable-next-line no-undef
  return { auditRecords, loading, error, refetch: fetchFieldAudit };
};

/**
 * Hook for fetching most recent audit record
 */
export const useMostRecentAudit = (transactionId, enabled = true) => {
  const [recentAudit, setRecentAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRecentAudit = async () => {
    if (!transactionId || !enabled) return;

    try {
      setLoading(true);
      setError(null);

      const response = await AppService.get(`/v1/transactions/${transactionId}/audit/latest`);

      // AppService interceptor unwraps response.data, so response is already the data object
      if (response && response.success) {
        setRecentAudit(response.data);
      } else {
        setRecentAudit(null);
      }
    } catch (err) {
      console.error('Failed to fetch recent audit:', err);
      setError(err.response?.data?.message || 'Failed to fetch recent audit');
      setRecentAudit(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecentAudit();
  }, [transactionId, enabled]);

  // eslint-disable-next-line no-undef
  return { recentAudit, loading, error, refetch: fetchRecentAudit };
};

/**
 * Hook for fetching audit count
 */
export const useTransactionAuditCount = (transactionId, enabled = true) => {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCount = async () => {
    if (!transactionId || !enabled) return;

    try {
      setLoading(true);
      setError(null);

      const response = await AppService.get(`/v1/transactions/${transactionId}/audit/count`);

      // AppService interceptor unwraps response.data, so response is already the data object
      if (response && response.success) {
        setCount(response.count || 0);
      } else {
        setCount(0);
      }
    } catch (err) {
      console.error('Failed to fetch audit count:', err);
      setError(err.response?.data?.message || 'Failed to fetch audit count');
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCount();
  }, [transactionId, enabled]);

  // eslint-disable-next-line no-undef
  return { count, loading, error, refetch: fetchCount };
};

export default useTransactionAudit;
